import { useEffect, useState } from 'react';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from '@azure/msal-react';
import { SignIn } from './components/SignIn';
import { AccessDenied } from './components/AccessDenied';
import { UserManagement } from './components/UserManagement';
import { loadProfile, type ProfileResult } from './lib/auth';

type View = 'home' | 'users';

function AuthorizedShell() {
  const { accounts } = useMsal();
  const account = accounts[0];
  const [state, setState] = useState<ProfileResult | { status: 'loading' } | { status: 'error'; message: string }>({
    status: 'loading',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await loadProfile();
        if (!cancelled) setState(result);
      } catch (err) {
        if (!cancelled) {
          setState({
            status: 'error',
            message: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'loading') return <CenteredMessage text="Loading…" />;
  if (state.status === 'error') return <CenteredMessage text={`Error: ${state.message}`} />;
  if (state.status === 'access_denied') {
    return <AccessDenied message={state.message} email={account?.username} />;
  }
  if (state.status === 'unauthorized') {
    return <CenteredMessage text="Session expired. Please refresh." />;
  }

  const { profile } = state;
  return <Shell profile={profile} />;
}

function Shell({ profile }: { profile: { id: string; email: string; displayName: string; role: 'User' | 'Manager' | 'Admin' } }) {
  const [view, setView] = useState<View>('home');
  return (
    <div style={shellStyles.wrap}>
      <header style={shellStyles.header}>
        <h1 style={shellStyles.title}>Irrigation Inspector</h1>
        <nav style={shellStyles.nav}>
          <button
            type="button"
            style={view === 'home' ? shellStyles.navActive : shellStyles.navBtn}
            onClick={() => setView('home')}
          >
            Home
          </button>
          {profile.role === 'Admin' ? (
            <button
              type="button"
              style={view === 'users' ? shellStyles.navActive : shellStyles.navBtn}
              onClick={() => setView('users')}
            >
              Users
            </button>
          ) : null}
        </nav>
        <div style={shellStyles.who}>
          <strong>{profile.displayName}</strong> · {profile.role}
        </div>
      </header>

      {view === 'home' ? (
        <div style={shellStyles.content}>
          <p style={shellStyles.line}>
            Signed in as <strong>{profile.email}</strong>.
          </p>
          <p style={shellStyles.note}>
            M2 core data layer is reachable. Master data, reports, and inspections endpoints are live.
            UI screens for the field workflow land in M3+.
          </p>
        </div>
      ) : null}

      {view === 'users' && profile.role === 'Admin' ? <UserManagement /> : null}
    </div>
  );
}

function CenteredMessage({ text }: { text: string }) {
  return (
    <div style={shellStyles.center}>
      <p>{text}</p>
    </div>
  );
}

export default function App() {
  const isAuthenticated = useIsAuthenticated();
  return (
    <>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>{isAuthenticated ? <AuthorizedShell /> : null}</AuthenticatedTemplate>
    </>
  );
}

const shellStyles: Record<string, React.CSSProperties> = {
  wrap: {
    minHeight: '100vh',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    background: '#f9fafb',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
    padding: '1rem 2rem',
    background: '#fff',
    borderBottom: '1px solid #e5e7eb',
  },
  title: { fontSize: '1.2rem', fontWeight: 600, margin: 0, flex: '0 0 auto' },
  nav: { display: 'flex', gap: '0.5rem', flex: '1 1 auto' },
  navBtn: {
    padding: '0.4rem 0.8rem',
    background: 'transparent',
    color: '#374151',
    border: '1px solid transparent',
    borderRadius: '0.3rem',
    cursor: 'pointer',
  },
  navActive: {
    padding: '0.4rem 0.8rem',
    background: '#eff6ff',
    color: '#1d4ed8',
    border: '1px solid #bfdbfe',
    borderRadius: '0.3rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  who: { color: '#6b7280', fontSize: '0.9rem' },
  content: { padding: '2rem' },
  line: { color: '#374151', margin: '0 0 0.5rem' },
  note: { color: '#6b7280', fontSize: '0.9rem' },
  center: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    color: '#374151',
  },
};
